// client/src/pages/adminRefunds.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import showNotification from '../../utilities/showNotification';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import BackButton from '../../components/customs/backButton';
import CustomAlert from '../../components/customs/customAlert'; // adjust path as needed
import useStudent from '../../hooks/useStudent';
import { GlobalContext } from '../../context/provider';

import '../../styles/globalstyles.css';
import './adminDashboard.css';

const AdminRefunds = () => {
    const navigate = useNavigate();
    const { authState: { authData } } = useContext(GlobalContext);
    const { getAllStudents, processRefund } = useStudent();

    const [isLoading, setIsLoading] = useState(true);
    const [pendingRefunds, setPendingRefunds] = useState([]);
    const [processingId, setProcessingId] = useState(null);
    const [alertConfig, setAlertConfig] = useState({ visible: false, message: '', action: null, loginId: null, paymentId: null });


    useEffect(() => {
        loadPendingRefunds();
    }, []);

    const loadPendingRefunds = async () => {
        setIsLoading(true);
        try {
            const response = await getAllStudents();
            if (response.success) {
                const refunds = [];
                (response.data || []).forEach(student => {
                    (student.paymentHistory || [])
                        .filter(p => p.status === 'refund_pending')
                        .forEach(payment => {
                            refunds.push({
                                student: {
                                    loginId: student.loginId,
                                    name: student.name,
                                    email: student.email,
                                    studentId: student.studentId
                                },
                                payment
                            });
                        });
                });
                // Sort by refundRequestedAt, newest first
                refunds.sort((a, b) => 
                    new Date(b.payment.refundRequestedAt) - new Date(a.payment.refundRequestedAt)
                );
                setPendingRefunds(refunds);
            }
        } catch (error) {
            console.error('Failed to load refunds:', error);
            showNotification('Failed to load refunds', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleProcessRefund = (loginId, paymentId, action) => {
        setAlertConfig({
            visible: true,
            loginId,
            paymentId,
            action,
            message: action === 'approve'
                ? 'Approve this refund? This will process the Stripe refund immediately.'
                : 'Reject this refund request?'
        });
    };

    const handleConfirmRefund = async () => {
        const { loginId, paymentId, action } = alertConfig;
        setAlertConfig(prev => ({ ...prev, visible: false }));
        setProcessingId(paymentId);
        try {
            const response = await processRefund(loginId, paymentId, action);
            if (response.success) {
                showNotification(
                    action === 'approve' ? '✅ Refund processed successfully' : '❌ Refund rejected',
                    'success'
                );
                setPendingRefunds(prev =>
                    prev.filter(r => r.payment.paymentId !== paymentId)
                );
            } else {
                showNotification(response.message || 'Failed to process refund', 'error');
            }
        } catch (error) {
            console.error('Refund error:', error);
            showNotification('Failed to process refund', 'error');
        } finally {
            setProcessingId(null);
        }
    };

    const formatDate = (date) => date 
        ? new Date(date).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
        : '—';

    return (
        <div className="ai-notebook-page" style={{height: '94vh'}}>
            {/* <div className="main-content"> */}
            <div className="header-row">
                <ProfileHeader
                    data={authData}
                    // title="💰 Refund Management"
                    activePage="admindashboard"
                />
                <div className="headerMiddle">
                    <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
                </div>
                <SharedHeader notificationCount={0} icons={[]} />
            </div>

            {/* Toolbar */}
            <div className="ai-notebook-toolbar">
                <div className="ai-notebook-toolbar-left">
                    <BackButton 
                        to="/admin/dashboard"
                        activeTab="management"
                        label="←"
                        bgColor="#6b7280"
                    />
                    <span className="ai-notebook-flow-name">
                        <span className="ai-notebook-lab-tag">💰 Admin Refund Management</span>
                        {/* <span className="ai-notebook-session-id">📚 Course: {courseInfo?.title} ({courseInfo?.courseId})</span> */}
                    </span>
                </div>
            </div>

            <div className="ai-notebook-toolbar-right">
                <button onClick={loadPendingRefunds} style={{
                    padding: '8px 16px', background: '#3b82f6', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer'
                }}>
                    🔄 Refresh
                </button>
            </div>

            <div className="ai-notebook-content">
                {isLoading ? (
                    <div className="loading-container">
                        <div className="loader"></div>
                        <p>Loading refund requests...</p>
                    </div>
                ) : pendingRefunds.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                        <h3>No pending refund requests</h3>
                        <p>All refund requests have been processed</p>
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
                            {pendingRefunds.length} pending refund{pendingRefunds.length > 1 ? 's' : ''}
                        </div>
                        {pendingRefunds.map(({ student, payment }) => (
                            <div key={payment.paymentId} className="billing-payment-card" style={{ marginBottom: '16px' }}>
                                
                                {/* Student Info */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '15px' }}>{student.name}</div>
                                        <div style={{ color: '#666', fontSize: '13px' }}>{student.email}</div>
                                        <div style={{ color: '#999', fontSize: '12px' }}>ID: {student.studentId}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 700, fontSize: '18px', color: '#2f62ee' }}>
                                            {payment.currency} {payment.amount.toFixed(2)}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#999' }}>
                                            Requested: {formatDate(payment.refundRequestedAt)}
                                        </div>
                                    </div>
                                </div>

                                <div className="billing-payment-divider" />

                                {/* Payment Info */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', margin: '12px 0', fontSize: '13px' }}>
                                    <div>
                                        <span style={{ color: '#999' }}>Payment ID: </span>
                                        <span>{payment.paymentId}</span>
                                    </div>
                                    <div>
                                        <span style={{ color: '#999' }}>Payment Date: </span>
                                        <span>{formatDate(payment.transactionDate)}</span>
                                    </div>
                                    <div>
                                        <span style={{ color: '#999' }}>Type: </span>
                                        <span>{payment.isInstallment ? '📅 Installment Plan' : '💳 One-time Payment'}</span>
                                    </div>
                                    <div>
                                        <span style={{ color: '#999' }}>Course(s): </span>
                                        <span>{payment.cartItems?.map(i => i.courseName).join(', ') || '—'}</span>
                                    </div>
                                    {payment.isInstallment && (
                                        <div>
                                            <span style={{ color: '#999' }}>Installment: </span>
                                            <span>#{payment.installmentNumber} of {payment.installmentPlan?.split('_')[1]}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Refund Reason */}
                                {payment.refundReason && (
                                    <div style={{ 
                                        background: '#f8f9fa', borderRadius: '8px', 
                                        padding: '12px', margin: '12px 0', fontSize: '13px'
                                    }}>
                                        <div style={{ fontWeight: 600, marginBottom: '4px', color: '#444' }}>
                                            💬 Student's Reason:
                                        </div>
                                        <div style={{ color: '#555' }}>{payment.refundReason}</div>
                                    </div>
                                )}

                                <div className="billing-payment-divider" />

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '12px' }}>
                                    <button
                                        onClick={() => handleProcessRefund(student.loginId, payment.paymentId, 'reject')}
                                        disabled={processingId === payment.paymentId}
                                        style={{
                                            padding: '8px 20px', background: '#fff', color: '#e74c3c',
                                            border: '1.5px solid #e74c3c', borderRadius: '8px',
                                            cursor: 'pointer', fontWeight: 600, fontSize: '13px'
                                        }}
                                    >
                                        ❌ Reject
                                    </button>
                                    <button
                                        onClick={() => handleProcessRefund(student.loginId, payment.paymentId, 'approve')}
                                        disabled={processingId === payment.paymentId}
                                        style={{
                                            padding: '8px 20px', background: '#2d7a3a', color: '#fff',
                                            border: 'none', borderRadius: '8px',
                                            cursor: 'pointer', fontWeight: 600, fontSize: '13px'
                                        }}
                                    >
                                        {processingId === payment.paymentId ? '⏳ Processing...' : '✅ Approve Refund'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {/* </div> */}

            <CustomAlert
                visible={alertConfig.visible}
                title={alertConfig.action === 'approve' ? 'Approve Refund' : 'Reject Refund'}
                icon={alertConfig.action === 'approve' ? '✅' : '❌'}
                message={alertConfig.message}
                onConfirm={handleConfirmRefund}
                onCancel={() => setAlertConfig(prev => ({ ...prev, visible: false }))}
            />

        </div>
    );
};

export default AdminRefunds;